import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FileText, TrendingUp, TrendingDown, Users, Search, Eye } from 'lucide-react';
import { toast } from 'sonner';

const Reports = () => {
  const [activeTab, setActiveTab] = useState('daily');
  const [dailyReport, setDailyReport] = useState(null);
  const [periodReport, setPeriodReport] = useState(null);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [topClients, setTopClients] = useState([]);
  const [topSalesClients, setTopSalesClients] = useState([]);
  
  // Client search states
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedClientDetails, setSelectedClientDetails] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  
  // Period filter states
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [periodType, setPeriodType] = useState('mensual');
  const [periodNumber, setPeriodNumber] = useState(1);
  const [periodClients, setPeriodClients] = useState([]);
  
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchDailyReport();
    fetchLowStockProducts();
    fetchTopClients();
    fetchTopSalesClients();
  }, []);

  const fetchDailyReport = async (date = null) => {
    try {
      const params = date ? `?date=${date}` : '';
      const response = await axios.get(`/reports/daily${params}`);
      setDailyReport(response.data);
    } catch (error) {
      toast.error('Error al cargar informe diario');
    }
  };

  const fetchPeriodReport = async () => {
    if (!startDate || !endDate) {
      toast.error('Selecciona un rango de fechas');
      return;
    }

    try {
      const response = await axios.get(`/reports/period?start_date=${startDate}&end_date=${endDate}`);
      setPeriodReport(response.data);
    } catch (error) {
      toast.error('Error al cargar informe de período');
    }
  };

  const fetchLowStockProducts = async () => {
    try {
      const response = await axios.get('/reports/low-stock');
      setLowStockProducts(response.data);
    } catch (error) {
      toast.error('Error al cargar productos con stock bajo');
    }
  };

  const fetchTopClients = async () => {
    try {
      const response = await axios.get('/reports/top-clients');
      setTopClients(response.data);
    } catch (error) {
      console.error('Error al cargar clientes frecuentes');
    }
  };

  const fetchTopSalesClients = async () => {
    try {
      const response = await axios.get('/reports/top-sales-clients?limit=10');
      setTopSalesClients(response.data);
    } catch (error) {
      console.error('Error al cargar ventas por cliente');
    }
  };

  const searchClients = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const response = await axios.get(`/clients/search?query=${encodeURIComponent(query)}`);
      setSearchResults(response.data);
    } catch (error) {
      toast.error('Error al buscar clientes');
    }
  };

  const fetchClientDetails = async (clientId) => {
    try {
      const response = await axios.get(`/clients/${clientId}/details`);
      setSelectedClientDetails(response.data);
      setIsDetailsModalOpen(true);
    } catch (error) {
      toast.error('Error al cargar detalles del cliente');
    }
  };

  const fetchClientsByPeriod = async () => {
    try {
      let params = `year=${selectedYear}&period_type=${periodType}`;
      if (periodType !== 'anual') {
        params += `&period_number=${periodNumber}`;
      }
      const response = await axios.get(`/reports/clients-by-period?${params}`);
      setPeriodClients(response.data);
    } catch (error) {
      toast.error('Error al cargar clientes por periodo');
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    fetchDailyReport(date);
  };

  const setQuickRange = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  const getPeriodOptions = () => {
    if (periodType === 'mensual') {
      return Array.from({length: 12}, (_, i) => ({
        value: i + 1,
        label: new Date(2000, i, 1).toLocaleDateString('es', {month: 'long'})
      }));
    } else if (periodType === 'trimestral') {
      return [
        {value: 1, label: 'Trimestre 1 (Ene-Mar)'},
        {value: 2, label: 'Trimestre 2 (Abr-Jun)'},
        {value: 3, label: 'Trimestre 3 (Jul-Sep)'},
        {value: 4, label: 'Trimestre 4 (Oct-Dic)'}
      ];
    } else if (periodType === 'semestral') {
      return [
        {value: 1, label: 'Semestre 1 (Ene-Jun)'},
        {value: 2, label: 'Semestre 2 (Jul-Dic)'}
      ];
    }
    return [];
  };

  useEffect(() => {
    if (searchQuery.length >= 2) {
      const timer = setTimeout(() => {
        searchClients(searchQuery);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  return (
    <div className="reports-page" data-testid="reports-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Informes</h1>
          <p className="page-subtitle">Analiza el rendimiento del negocio</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="reports-tabs">
        <TabsList className="tabs-list">
          <TabsTrigger value="daily" data-testid="tab-daily">Diario</TabsTrigger>
          <TabsTrigger value="period" data-testid="tab-period">Período</TabsTrigger>
          <TabsTrigger value="stock" data-testid="tab-stock">Stock</TabsTrigger>
          <TabsTrigger value="clients" data-testid="tab-clients">Clientes</TabsTrigger>
          <TabsTrigger value="client-periods" data-testid="tab-client-periods">Clientes por Periodo</TabsTrigger>
        </TabsList>

        <TabsContent value="daily" className="tab-content">
          <Card className="date-selector">
            <Label htmlFor="daily-date">Seleccionar Fecha</Label>
            <div className="date-controls">
              <Input
                id="daily-date"
                type="date"
                value={selectedDate}
                onChange={(e) => handleDateChange(e.target.value)}
                data-testid="daily-date-input"
              />
              <Button onClick={() => handleDateChange(new Date().toISOString().split('T')[0])}>
                Hoy
              </Button>
            </div>
          </Card>

          {dailyReport && (
            <>
              <div className="stats-grid">
                <Card className="stat-card">
                  <div className="stat-content">
                    <div className="stat-icon" style={{ background: '#d1fae5' }}>
                      <TrendingUp size={24} style={{ color: '#10b981' }} />
                    </div>
                    <div>
                      <p className="stat-label">Ingresos Totales</p>
                      <h2 className="stat-value" data-testid="daily-total-income">${dailyReport.total_income.toFixed(2)}</h2>
                      <p className="stat-subtitle">Ventas + Suscripciones</p>
                    </div>
                  </div>
                </Card>

                <Card className="stat-card">
                  <div className="stat-content">
                    <div className="stat-icon" style={{ background: '#dbeafe' }}>
                      <FileText size={24} style={{ color: '#3b82f6' }} />
                    </div>
                    <div>
                      <p className="stat-label">Ventas</p>
                      <h2 className="stat-value">${dailyReport.total_sales.toFixed(2)}</h2>
                      <p className="stat-subtitle">{dailyReport.sales_count} transacciones</p>
                    </div>
                  </div>
                </Card>

                <Card className="stat-card">
                  <div className="stat-content">
                    <div className="stat-icon" style={{ background: '#ede9fe' }}>
                      <FileText size={24} style={{ color: '#8b5cf6' }} />
                    </div>
                    <div>
                      <p className="stat-label">Suscripciones</p>
                      <h2 className="stat-value">${dailyReport.total_subscriptions.toFixed(2)}</h2>
                      <p className="stat-subtitle">{dailyReport.subscriptions_count} nuevas</p>
                    </div>
                  </div>
                </Card>
              </div>

              {Object.keys(dailyReport.product_sales).length > 0 && (
                <Card className="products-report">
                  <h3 className="section-title">Productos Vendidos</h3>
                  <div className="products-list">
                    {Object.entries(dailyReport.product_sales).map(([name, data]) => (
                      <div key={name} className="product-row" data-testid={`product-row-${name}`}>
                        <span className="product-name">{name}</span>
                        <div className="product-stats">
                          <span className="product-quantity">x{data.quantity}</span>
                          <span className="product-total">${data.total.toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="period" className="tab-content">
          <Card className="period-selector">
            <div className="period-controls">
              <div className="date-inputs">
                <div className="form-group">
                  <Label htmlFor="start-date">Fecha Inicio</Label>
                  <Input
                    id="start-date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    data-testid="start-date-input"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="end-date">Fecha Fin</Label>
                  <Input
                    id="end-date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    data-testid="end-date-input"
                  />
                </div>
              </div>
              <div className="quick-ranges">
                <Button variant="outline" onClick={() => setQuickRange(7)} data-testid="btn-7-days">
                  Última Semana
                </Button>
                <Button variant="outline" onClick={() => setQuickRange(30)}>
                  Último Mes
                </Button>
                <Button onClick={fetchPeriodReport} data-testid="generate-report-btn">
                  Generar Informe
                </Button>
              </div>
            </div>
          </Card>

          {periodReport && (
            <>
              <div className="stats-grid">
                <Card className="stat-card">
                  <div className="stat-content">
                    <div className="stat-icon" style={{ background: '#d1fae5' }}>
                      <TrendingUp size={24} style={{ color: '#10b981' }} />
                    </div>
                    <div>
                      <p className="stat-label">Ingresos Totales</p>
                      <h2 className="stat-value" data-testid="period-total-income">${periodReport.total_income.toFixed(2)}</h2>
                    </div>
                  </div>
                </Card>

                <Card className="stat-card">
                  <div className="stat-content">
                    <div className="stat-icon" style={{ background: '#fee2e2' }}>
                      <TrendingDown size={24} style={{ color: '#ef4444' }} />
                    </div>
                    <div>
                      <p className="stat-label">Gastos Totales</p>
                      <h2 className="stat-value" data-testid="period-total-expenses">${periodReport.total_expenses.toFixed(2)}</h2>
                    </div>
                  </div>
                </Card>

                <Card className={`stat-card ${periodReport.profit >= 0 ? 'profit-positive' : 'profit-negative'}`}>
                  <div className="stat-content">
                    <div className="stat-icon" style={{ background: periodReport.profit >= 0 ? '#d1fae5' : '#fee2e2' }}>
                      {periodReport.profit >= 0 ? (
                        <TrendingUp size={24} style={{ color: '#10b981' }} />
                      ) : (
                        <TrendingDown size={24} style={{ color: '#ef4444' }} />
                      )}
                    </div>
                    <div>
                      <p className="stat-label">Ganancia</p>
                      <h2 className="stat-value" data-testid="period-profit">${periodReport.profit.toFixed(2)}</h2>
                      <p className="stat-subtitle">Ingresos - Gastos</p>
                    </div>
                  </div>
                </Card>
              </div>

              <div className="period-details">
                <Card className="details-card">
                  <h4>Detalle del Período</h4>
                  <div className="detail-row">
                    <span>Ventas:</span>
                    <span>${periodReport.total_sales.toFixed(2)} ({periodReport.sales_count})</span>
                  </div>
                  <div className="detail-row">
                    <span>Suscripciones:</span>
                    <span>${periodReport.total_subscriptions.toFixed(2)} ({periodReport.subscriptions_count})</span>
                  </div>
                  <div className="detail-row">
                    <span>Gastos:</span>
                    <span>${periodReport.total_expenses.toFixed(2)} ({periodReport.expenses_count})</span>
                  </div>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="stock" className="tab-content">
          <Card className="stock-report">
            <h3 className="section-title">Productos con Stock Bajo</h3>
            {lowStockProducts.length === 0 ? (
              <div className="empty-message">No hay productos con stock bajo</div>
            ) : (
              <div className="stock-list">
                {lowStockProducts.map((product) => (
                  <div key={product.id} className="stock-item" data-testid={`low-stock-${product.id}`}>
                    <div>
                      <div className="product-name">{product.name}</div>
                      <div className="product-code">{product.code}</div>
                    </div>
                    <div className="stock-badge low">{product.stock} unidades</div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="clients" className="tab-content">
          {/* Client Search Section */}
          <Card className="client-search-card">
            <h3 className="section-title">
              <Search size={20} style={{ display: 'inline-block', marginRight: '0.5rem' }} />
              Buscar Cliente
            </h3>
            <div className="search-box-container">
              <Input
                type="text"
                placeholder="Buscar por nombre del cliente..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="client-search-input"
              />
            </div>
            
            {searchResults.length > 0 && (
              <div className="search-results-list">
                {searchResults.map((client) => (
                  <div key={client.id} className="search-result-item">
                    <div className="result-info">
                      <div className="result-name">{client.name}</div>
                      <div className="result-email">{client.email}</div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => fetchClientDetails(client.id)}
                      className="view-details-btn"
                    >
                      <Eye size={16} />
                      Ver Detalles
                    </Button>
                  </div>
                ))}
              </div>
            )}
            
            {searchQuery.length >= 2 && searchResults.length === 0 && (
              <div className="empty-message">No se encontraron clientes</div>
            )}
          </Card>

          {/* Top Clients by Sales */}
          <Card className="clients-report">
            <h3 className="section-title">
              <Users size={24} style={{ display: 'inline-block', marginRight: '0.5rem' }} />
              Mejores Clientes por Ventas
            </h3>
            {topSalesClients.length === 0 ? (
              <div className="empty-message">No hay datos de ventas por cliente</div>
            ) : (
              <div className="clients-list">
                {topSalesClients.map((item, index) => (
                  <div key={index} className="client-item" data-testid={`top-sales-client-${index}`}>
                    <div className="client-rank">{index + 1}</div>
                    <div className="client-info">
                      <div className="client-name">{item.cliente_nombre || 'Cliente'}</div>
                      <div className="client-stats">
                        {item.sales_count} {item.sales_count === 1 ? 'venta' : 'ventas'}
                      </div>
                    </div>
                    <div className="client-total">${item.total_amount.toFixed(2)}</div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Top Clients by Subscriptions */}
          <Card className="clients-report">
            <h3 className="section-title">Mejores Clientes por Suscripciones</h3>
            {topClients.length === 0 ? (
              <div className="empty-message">No hay datos de suscripciones</div>
            ) : (
              <div className="clients-list">
                {topClients.map((item, index) => (
                  <div key={item.client.id} className="client-item" data-testid={`top-client-${index}`}>
                    <div className="client-rank">{index + 1}</div>
                    <div className="client-info">
                      <div className="client-name">{item.client.name}</div>
                      <div className="client-stats">
                        {item.subscription_count} suscripciones
                      </div>
                    </div>
                    <div className="client-total">${item.total_spent.toFixed(2)}</div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="client-periods" className="tab-content">
          <Card className="period-filter-card">
            <h3 className="section-title">Filtrar Clientes por Periodo</h3>
            
            <div className="period-filters">
              <div className="filter-row">
                <div className="form-group">
                  <Label>Año</Label>
                  <Select value={selectedYear.toString()} onValueChange={(val) => setSelectedYear(parseInt(val))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2023">2023</SelectItem>
                      <SelectItem value="2024">2024</SelectItem>
                      <SelectItem value="2025">2025</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="form-group">
                  <Label>Tipo de Periodo</Label>
                  <Select value={periodType} onValueChange={(val) => {
                    setPeriodType(val);
                    setPeriodNumber(1);
                  }}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mensual">Mensual</SelectItem>
                      <SelectItem value="trimestral">Trimestral</SelectItem>
                      <SelectItem value="semestral">Semestral</SelectItem>
                      <SelectItem value="anual">Anual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {periodType !== 'anual' && (
                  <div className="form-group">
                    <Label>Periodo</Label>
                    <Select value={periodNumber.toString()} onValueChange={(val) => setPeriodNumber(parseInt(val))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {getPeriodOptions().map(option => (
                          <SelectItem key={option.value} value={option.value.toString()}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <Button onClick={fetchClientsByPeriod} className="filter-btn">
                Aplicar Filtros
              </Button>
            </div>
          </Card>

          {periodClients.length > 0 && (
            <Card className="period-results-card">
              <h3 className="section-title">
                Resultados - {periodType.charAt(0).toUpperCase() + periodType.slice(1)} {selectedYear}
              </h3>
              <div className="period-table">
                <div className="table-header">
                  <div className="col-name">Cliente</div>
                  <div className="col-count">Ventas</div>
                  <div className="col-amount">Monto Total</div>
                </div>
                {periodClients.map((client, index) => (
                  <div key={index} className="table-row">
                    <div className="col-name">{client.cliente_nombre}</div>
                    <div className="col-count">{client.sales_count + client.subscriptions_count}</div>
                    <div className="col-amount">${client.grand_total.toFixed(2)}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {periodClients.length === 0 && (
            <Card className="empty-card">
              <div className="empty-message">
                Selecciona un periodo y haz clic en "Aplicar Filtros" para ver los resultados
              </div>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Client Details Modal */}
      <Dialog open={isDetailsModalOpen} onOpenChange={setIsDetailsModalOpen}>
        <DialogContent className="details-modal">
          <DialogHeader>
            <DialogTitle>Detalles del Cliente</DialogTitle>
          </DialogHeader>
          {selectedClientDetails && (
            <div className="details-content">
              <div className="client-info-header">
                <h3>{selectedClientDetails.client.name}</h3>
                <p>{selectedClientDetails.client.email}</p>
              </div>

              <div className="totals-summary">
                <div className="total-box">
                  <p>Ventas</p>
                  <h4>${selectedClientDetails.sales_total.toFixed(2)}</h4>
                  <span>{selectedClientDetails.sales_count} transacciones</span>
                </div>
                <div className="total-box">
                  <p>Suscripciones</p>
                  <h4>${selectedClientDetails.subscriptions_total.toFixed(2)}</h4>
                  <span>{selectedClientDetails.subscriptions_count} suscripciones</span>
                </div>
                <div className="total-box highlight">
                  <p>Total General</p>
                  <h4>${selectedClientDetails.grand_total.toFixed(2)}</h4>
                </div>
              </div>

              <div className="details-section">
                <h4>Ventas Recientes</h4>
                {selectedClientDetails.sales.length === 0 ? (
                  <p className="empty-text">No hay ventas registradas</p>
                ) : (
                  <div className="details-list">
                    {selectedClientDetails.sales.slice(0, 5).map((sale) => (
                      <div key={sale.id} className="detail-item">
                        <div>
                          <span className="item-id">#{sale.sale_number || sale.id.slice(0, 8)}</span>
                          <span className="item-date">
                            {new Date(sale.created_at).toLocaleDateString('es')}
                          </span>
                        </div>
                        <span className="item-amount">${sale.total_amount.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="details-section">
                <h4>Suscripciones</h4>
                {selectedClientDetails.subscriptions.length === 0 ? (
                  <p className="empty-text">No hay suscripciones registradas</p>
                ) : (
                  <div className="details-list">
                    {selectedClientDetails.subscriptions.slice(0, 5).map((sub) => (
                      <div key={sub.id} className="detail-item">
                        <div>
                          <span className="item-id">{sub.subscription_type}</span>
                          <span className="item-date">
                            {new Date(sub.start_date).toLocaleDateString('es')} - {new Date(sub.end_date).toLocaleDateString('es')}
                          </span>
                        </div>
                        <span className="item-amount">${sub.price.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <style jsx>{`
        .reports-page {
          max-width: 1400px;
        }

        .page-header {
          margin-bottom: 2rem;
        }

        .page-title {
          font-size: 2rem;
          font-weight: 800;
          color: #1e293b;
          margin-bottom: 0.5rem;
        }

        .page-subtitle {
          color: #64748b;
        }

        .reports-tabs {
          margin-top: 2rem;
        }

        .tabs-list {
          background: white;
          padding: 0.5rem;
          border-radius: 10px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .tab-content {
          margin-top: 2rem;
        }

        .date-selector,
        .period-selector {
          padding: 1.5rem;
          margin-bottom: 2rem;
        }

        .date-controls {
          display: flex;
          gap: 1rem;
          margin-top: 0.5rem;
        }

        .period-controls {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .date-inputs {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .quick-ranges {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .stat-card {
          padding: 1.5rem;
          border: none;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .stat-content {
          display: flex;
          gap: 1.5rem;
          align-items: flex-start;
        }

        .stat-icon {
          width: 56px;
          height: 56px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .stat-label {
          font-size: 0.875rem;
          color: #64748b;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .stat-value {
          font-size: 2rem;
          font-weight: 800;
          color: #1e293b;
          margin-bottom: 0.25rem;
        }

        .stat-subtitle {
          font-size: 0.875rem;
          color: #64748b;
        }

        .profit-positive .stat-value {
          color: #10b981;
        }

        .profit-negative .stat-value {
          color: #ef4444;
        }

        .products-report,
        .stock-report,
        .clients-report {
          padding: 1.5rem;
          margin-bottom: 1.5rem;
        }

        .section-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 1.5rem;
        }

        .products-list,
        .stock-list,
        .clients-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .product-row,
        .stock-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          background: #f8fafc;
          border-radius: 8px;
        }

        .product-name,
        .client-name {
          font-weight: 600;
          color: #1e293b;
        }

        .product-code {
          font-size: 0.875rem;
          color: #64748b;
          font-family: monospace;
        }

        .product-stats {
          display: flex;
          gap: 1rem;
          align-items: center;
        }

        .product-quantity {
          font-weight: 600;
          color: #64748b;
        }

        .product-total {
          font-size: 1.125rem;
          font-weight: 700;
          color: #10b981;
        }

        .stock-badge {
          padding: 0.5rem 1rem;
          border-radius: 6px;
          font-weight: 700;
          font-size: 0.875rem;
        }

        .stock-badge.low {
          background: #fee2e2;
          color: #991b1b;
        }

        .client-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          background: #f8fafc;
          border-radius: 8px;
        }

        .client-rank {
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 1.125rem;
        }

        .client-info {
          flex: 1;
        }

        .client-stats {
          font-size: 0.875rem;
          color: #64748b;
        }

        .client-total {
          font-size: 1.25rem;
          font-weight: 700;
          color: #10b981;
        }

        .period-details {
          margin-top: 2rem;
        }

        .details-card {
          padding: 1.5rem;
        }

        .details-card h4 {
          font-size: 1.125rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 1rem;
        }

        .detail-row {
          display: flex;
          justify-content: space-between;
          padding: 0.75rem 0;
          border-bottom: 1px solid #e2e8f0;
          font-size: 0.9375rem;
        }

        .detail-row span:first-child {
          color: #64748b;
        }

        .detail-row span:last-child {
          font-weight: 600;
          color: #1e293b;
        }

        .empty-message {
          text-align: center;
          padding: 3rem;
          color: #94a3b8;
        }

        /* Client Search Styles */
        .client-search-card {
          padding: 1.5rem;
          margin-bottom: 1.5rem;
        }

        .search-box-container {
          margin-bottom: 1rem;
        }

        .client-search-input {
          width: 100%;
        }

        .search-results-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          margin-top: 1rem;
        }

        .search-result-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          background: #f8fafc;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
        }

        .result-info {
          flex: 1;
        }

        .result-name {
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 0.25rem;
        }

        .result-email {
          font-size: 0.875rem;
          color: #64748b;
        }

        .view-details-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        /* Period Filter Styles */
        .period-filter-card {
          padding: 1.5rem;
          margin-bottom: 1.5rem;
        }

        .period-filters {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .filter-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
        }

        .filter-btn {
          width: fit-content;
        }

        .period-results-card {
          padding: 1.5rem;
        }

        .period-table {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .table-header {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr;
          gap: 1rem;
          padding: 0.75rem;
          background: #f1f5f9;
          border-radius: 6px;
          font-weight: 600;
          color: #475569;
          font-size: 0.875rem;
        }

        .table-row {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr;
          gap: 1rem;
          padding: 0.75rem;
          background: #f8fafc;
          border-radius: 6px;
          align-items: center;
        }

        .col-name {
          font-weight: 600;
          color: #1e293b;
        }

        .col-count {
          color: #64748b;
        }

        .col-amount {
          font-weight: 700;
          color: #10b981;
        }

        .empty-card {
          padding: 3rem;
        }

        /* Modal Styles */
        .details-modal {
          max-width: 700px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .details-content {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          margin-top: 1rem;
        }

        .client-info-header {
          text-align: center;
          padding-bottom: 1rem;
          border-bottom: 2px solid #e2e8f0;
        }

        .client-info-header h3 {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 0.25rem;
        }

        .client-info-header p {
          color: #64748b;
        }

        .totals-summary {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 1rem;
        }

        .total-box {
          padding: 1rem;
          background: #f8fafc;
          border-radius: 8px;
          text-align: center;
        }

        .total-box.highlight {
          background: #dcfce7;
        }

        .total-box p {
          font-size: 0.875rem;
          color: #64748b;
          margin-bottom: 0.5rem;
        }

        .total-box h4 {
          font-size: 1.5rem;
          font-weight: 800;
          color: #10b981;
          margin-bottom: 0.25rem;
        }

        .total-box span {
          font-size: 0.75rem;
          color: #64748b;
        }

        .details-section h4 {
          font-size: 1.125rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 1rem;
        }

        .details-list {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .detail-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem;
          background: #f8fafc;
          border-radius: 6px;
        }

        .item-id {
          font-weight: 600;
          color: #1e293b;
          margin-right: 1rem;
        }

        .item-date {
          font-size: 0.875rem;
          color: #64748b;
        }

        .item-amount {
          font-weight: 700;
          color: #10b981;
        }

        .empty-text {
          text-align: center;
          padding: 2rem;
          color: #94a3b8;
        }

        @media (max-width: 768px) {
          .date-inputs {
            grid-template-columns: 1fr;
          }

          .quick-ranges {
            flex-direction: column;
          }

          .stats-grid {
            grid-template-columns: 1fr;
          }

          .stat-value {
            font-size: 1.5rem;
          }

          .filter-row {
            grid-template-columns: 1fr;
          }

          .table-header,
          .table-row {
            grid-template-columns: 1fr;
          }

          .totals-summary {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default Reports;
